import React, { useState, useEffect } from 'react';
import { Btn, SectionHeader, Badge, StatCard, RNLLoader } from '../components/UI';
import { fmtDate, fmt } from '../utils';
import * as api from '../api';

export default function AlertsPage({ toast }) {
  const [alerts, setAlerts] = useState([]);
  const [count,  setCount]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all'); // all | unread | critical

  const load = async () => {
    setLoading(true);
    try {
      const ps = await api.listProjects();
      const projectsList = Array.isArray(ps) ? ps : [];
      
      const allAlerts = [];
      let totalCount = 0;
      let unreadCount = 0;
      let criticalCount = 0;
      let warningCount = 0;

      await Promise.all(projectsList.map(async (p) => {
        try {
          const a = await api.getAlerts(p.project_id);
          if (Array.isArray(a)) {
            allAlerts.push(...a.map(item => ({ ...item, project_name: p.project_name })));
          }
        } catch {}
      }));

      allAlerts.sort((x, y) => new Date(y.created_at) - new Date(x.created_at));

      setAlerts(allAlerts);
      setCount({
        total_count: allAlerts.length,
        unread_count: allAlerts.filter(a => !a.is_read).length,
        critical_count: allAlerts.filter(a => a.severity === 'critical' && !a.is_read).length,
        warning_count: allAlerts.filter(a => (a.severity === 'warning' || a.severity === 'high') && !a.is_read).length,
      });
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await api.markAlertRead(id); toast.show('Marked as read'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  const resolve = async (id) => {
    try { await api.resolveAlert(id); toast.show('Alert resolved'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };

  const filtered = alerts.filter(a => {
    if (filter === 'unread')   return !a.is_read;
    if (filter === 'critical') return a.severity === 'critical';
    return true;
  });

  const sevColor = { critical: 'red', high: 'red', medium: 'amber', low: 'green', info: 'cyan' };
  const sevIcon  = { critical: '🔴', high: '🟠', medium: '🟡', low: '🟢', info: '🔵' };

  return (
    <div className="fade-in">
      <SectionHeader title="Project Alerts" subtitle="Budget breaches, overdue orders and cost warnings" />

      {/* KPI row */}
      {count && (
        <div className="grid-cols-4" style={{ gap: 12, marginBottom: 20 }}>
          <StatCard label="Total Alerts"   value={count.total_count    || 0} />
          <StatCard label="Unread"         value={count.unread_count   || 0} color="amber" />
          <StatCard label="Critical"       value={count.critical_count || 0} color={count.critical_count > 0 ? 'red' : 'green'} />
          <StatCard label="Warnings"       value={count.warning_count  || 0} color="amber" />
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[
          { id: 'all',      label: `All (${alerts.length})` },
          { id: 'unread',   label: `Unread (${alerts.filter(a => !a.is_read).length})` },
          { id: 'critical', label: `Critical (${alerts.filter(a => a.severity === 'critical').length})` },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
            border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'var(--font)',
            background: filter === f.id ? 'var(--accent)' : 'var(--bg-elevated)',
            color:      filter === f.id ? '#fff'           : 'var(--text-secondary)',
          }}>{f.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <Btn variant="ghost" onClick={load}>↻ Refresh</Btn>
      </div>

      {/* Alert list */}
      {loading ? (
        <RNLLoader title="Loading Alerts..." subtitle="Fetching latest data" />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No alerts</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Everything looks good for this filter.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(a => (
            <div key={a.alert_id} style={{
              background: a.is_read ? 'var(--bg-card)' : 'var(--bg-elevated)',
              border: `1px solid ${a.severity === 'critical' ? 'rgba(239,68,68,0.35)' : a.severity === 'high' ? 'rgba(245,158,11,0.3)' : 'var(--border)'}`,
              borderRadius: 10, padding: '14px 16px',
              display: 'flex', alignItems: 'flex-start', gap: 14,
              opacity: a.is_resolved ? 0.6 : 1,
            }}>
              {/* Icon */}
              <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>
                {sevIcon[a.severity] || '⚪'}
              </span>

              {/* Body */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {a.project_name && <span style={{ color: 'var(--accent)', marginRight: 6 }}>[{a.project_name}]</span>}
                    {a.title}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <Badge color={sevColor[a.severity] || 'gray'}>{a.severity}</Badge>
                    {a.alert_type && <Badge color="gray">{a.alert_type}</Badge>}
                    {!a.is_read     && <Badge color="accent">Unread</Badge>}
                    {a.is_resolved  && <Badge color="green">Resolved</Badge>}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>{a.description}</div>
                {(a.actual_value || a.threshold_value) && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5, fontFamily: 'var(--mono)' }}>
                    Actual: {fmt(a.actual_value)} · Threshold: {fmt(a.threshold_value)}
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  {fmtDate(a.created_at)}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                {!a.is_read     && <Btn variant="ghost"   size="sm" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => markRead(a.alert_id)}>Mark Read</Btn>}
                {!a.is_resolved && <Btn variant="success" size="sm" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => resolve(a.alert_id)}>Resolve</Btn>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
